import fetchWithRetry from './fetchWithRetry';
import type RequestOptions from './RequestOptions';
import type MailboxRequestOptions from './MailboxRequestOptions';
import { getConfig } from './config';
import { getApp } from 'owa-config';
import { debugErrorThatWillShowErrorPopupOnly, trace } from 'owa-trace';
import getUrlWithAddedQueryParameters from 'owa-url/lib/getUrlWithAddedQueryParameters';
import { checkMailboxInfoForDiagnosticData } from './utils/checkMailboxInfoForDiagnosticData';
import { isServiceRequestSupportedForMailbox } from './utils/isServiceRequestSupportedForMailbox';
import { disallowedLicensingCallCheck } from './utils/disallowedLicensingCallCheck';
import { makeServiceRequestB2 } from './B2ServiceUtils';
import { getB2ServiceApiMode } from 'owa-b2-flight/lib/getB2ServiceApiMode';
import { type B2ServiceApiMode } from 'owa-b2-flight/lib/B2ServiceApiMode';

export function makeServiceRequest<T>(
    actionName: string,
    parameters: any,
    options?: RequestOptions | MailboxRequestOptions
): Promise<T> {
    trace.info(`ServiceAction ${actionName}`, 'service');

    checkMailboxInfoForDiagnosticData(options?.mailboxInfo);
    if (!isServiceRequestSupportedForMailbox(options?.mailboxInfo)) {
        const msg = `Cannot issue OWS request for a mailbox: ${options?.mailboxInfo?.type} that does not support service requests`;
        debugErrorThatWillShowErrorPopupOnly(msg);
        throw new Error(
            'Cannot issue OWS request for a mailbox that does not support service requests'
        );
    }

    // Check to make sure this is not a disallowed licensing call
    disallowedLicensingCallCheck(options?.mailboxInfo, actionName);

    if (options?.mailboxInfo?.isRemoved) {
        debugErrorThatWillShowErrorPopupOnly('InvalidRequest: MailboxInfo was removed');
        throw new Error('InvalidRequest: MailboxInfo was removed');
    }

    const config = getConfig();

    // Add TenantId to header for IIS log routing
    const tenantId = config.getTenantId(options?.mailboxInfo);
    if (tenantId) {
        options = options || ({} as RequestOptions);

        // Convert plain object to Headers instance if necessary
        if (!(options.headers instanceof Headers)) {
            const existingHeaders = options.headers || {};
            options.headers = new Headers(existingHeaders);
        }

        options.headers.set('X-TenantId', tenantId);
    }

    let endpoint;
    if (options?.endpoint) {
        endpoint = options.endpoint;
    } else {
        const baseUrl = options?.customBaseUrl
            ? options.customBaseUrlSubPath
                ? options.customBaseUrl.concat(options.customBaseUrlSubPath)
                : options.customBaseUrl
            : options?.customBaseUrlSubPath
            ? config.baseUrl.concat('/' + options.customBaseUrlSubPath)
            : config.baseUrl;
        endpoint = `${baseUrl}/service.svc?action=${actionName}`;
    }

    if (
        (config.isUserIdle !== undefined && config.isUserIdle()) ||
        options?.isUserActivity == false
    ) {
        endpoint = getUrlWithAddedQueryParameters(endpoint, { UA: '0' });
    }

    endpoint = getUrlWithAddedQueryParameters(
        endpoint,
        { app: getApp() },
        true /* ignore if present */
    );

    const b2ServiceModeForAction = getB2ServiceApiMode(
        actionName,
        config.getApplicationSettings,
        options?.mailboxInfo
    );

    // If B2 service is enabled for this action, route the request to B2 service
    if (b2ServiceModeForAction !== 0) {
        return makeServiceRequestB2<T>(
            actionName,
            endpoint,
            parameters,
            b2ServiceModeForAction,
            options
        );
    }

    return makeServiceRequestA<T>(actionName, endpoint, parameters, options);
}

export function makeServiceRequestA<T>(
    actionName: string,
    endpoint: string,
    parameters: any,
    options?: RequestOptions | MailboxRequestOptions
): Promise<T> {
    return fetchWithRetry(actionName, endpoint, 1, options, parameters);
}
